import { useMemo } from "react";
import { useClinicWhitelabel } from "./useClinicWhitelabel";
import { useAppTranslation } from "./useAppTranslation";
import { useUserRoles } from "./useUserRoles";
import { useLocation } from "react-router";
import { PRODUCTS, SECTIONS } from "@routes/routes";
import type { ListTab } from "@UI/components/Organisms/ListPage/listPage.types";

export const useListTabs = () => {
  const { t } = useAppTranslation();
  const { pathname } = useLocation();
  const { isMotherClinic, isPartnerClinic, getMotherClinicName } =
    useClinicWhitelabel();
  const isProductsView = pathname.startsWith(PRODUCTS);
  const isSectionsView = pathname.includes(SECTIONS);

  const regularTabs = useMemo(
    () => [
      {
        label: t("filters.tabs.all"),
        id: ListTabs.ALL,
      },
      {
        label: t("filters.tabs.latest"),
        id: ListTabs.LATEST,
      },
      {
        label: t("filters.tabs.favourite"),
        id: ListTabs.FAVORITE,
      },
      {
        label: t("filters.tabs.own"),
        id: ListTabs.OWN,
      },
      {
        label: isSectionsView ? t("common.templates") : "alloweat",
        id: ListTabs.ALLOWEAT,
      },
      ...(isPartnerClinic
        ? [
            {
              label: t("filters.tabs.common_clinic"),
              id: ListTabs.COMMON_CLINIC,
            },
          ]
        : []),
      ...(isMotherClinic
        ? [
            {
              label: getMotherClinicName(t("common.clinic")),
              id: ListTabs.MOTHER_CLINIC,
            },
          ]
        : []),
    ],
    [t, isSectionsView, isPartnerClinic, isMotherClinic, getMotherClinicName],
  );

  const adminTabs = useMemo(
    () => [
      ...regularTabs,
      ...(isProductsView
        ? [
            {
              label: t("filters.tabs.generic"),
              id: ListTabs.GENERIC,
            },
            {
              label: t("filters.tabs.synonyms"),
              id: ListTabs.SYNONYMS,
            },
          ]
        : []),
      {
        label: t("filters.tabs.unverified"),
        id: ListTabs.UNVERIFIED,
      },
    ],
    [t, regularTabs, isProductsView],
  );

  return {
    regularTabs,
    adminTabs,
  };
};

export const useLibraryTabs = (): ListTab<ListTabs>[] => {
  const { regularTabs, adminTabs } = useListTabs();
  const { isAdminClinicMember } = useUserRoles();

  return useMemo(
    () =>
      (isAdminClinicMember ? adminTabs : regularTabs).map(({ id, label }) => ({
        id,
        name: label,
      })),
    [isAdminClinicMember, adminTabs, regularTabs],
  );
};

export enum ListTabs {
  ALL = "all",
  LATEST = "latest",
  FAVORITE = "favorite",
  OWN = "own",
  GENERIC = "generic",
  SYNONYMS = "synonyms",
  UNVERIFIED = "reviewed",
  ALLOWEAT = "alloweat",
  COMMON_CLINIC = "commonClinic",
  MOTHER_CLINIC = "motherClinic",
}

export enum SourceTabs {
  ALLOWEAT = "1",
  CLINIC = "2",
  COMMON_CLINIC = "3",
  MOTHER_CLINIC = "4",
}
